import * as moment from 'moment-timezone';
import { environment } from '../../environments/environment';
import { EeDatePipe } from '../pipes/ee-date.pipe';
import { SelectItemInterface } from '../components/select/select.component';

export interface Sorting {
  sortBy: string;
  sortDirection: string;
}

export interface Filter {
  key: string;
  label: string;
  type: 'date' | 'text' | 'boolean' | 'number';
  value: any;
  placeholder?: string;
  editable: boolean;
}

export interface Filters {
  [key: string]: Filter;
}

export const FORM_FIELDS = {
  enterprise: {
    slug: 'enterprise',
    label: 'Entreprise',
    description: "Nom de l'entreprise / Collectivité",
  },
  mutuelle_id: {
    slug: 'mutuelle_id',
    label: "Numéro d'adhérent",
    description: "Si déjà adhérent mutuelle, numéro de l'adhérent",
  },
  name: {
    slug: 'name',
    label: 'Prénom',
    description: null,
  },
  lastname: {
    slug: 'lastname',
    label: 'Nom',
    description: null,
  },
  phone: {
    slug: 'phone',
    label: 'Téléphone',
    description: null,
  },
  email: {
    slug: 'email',
    label: 'Email',
    description: null,
  },
  address: {
    slug: 'address',
    label: 'Adresse',
    description: 'Rue, code postal, ville',
  },
  social_number: {
    slug: 'social_number',
    label: 'Numéro de sécurité sociale',
    description: null,
  },
  scheme: {
    slug: 'scheme',
    label: 'Régime',
    description: 'Régime général / Régime local',
  },
  family_situation: {
    slug: 'family_situation',
    label: 'Situation familiale',
    description: 'Célibataire, marié(e), pacsé(e), divorcé(e), veuf(ve)',
  },
  qualification: {
    slug: 'qualification',
    label: 'Qualification',
    description: 'Cadre, non-cadre',
  },
  spouse: {
    slug: 'spouse',
    label: 'Conjoint(e)',
    description: null,
  },
  children: {
    slug: 'children',
    label: 'Enfants et étudiants',
    description: null,
  },
  affiliation: {
    slug: 'affiliation',
    label: 'Affiliation',
    description:
      "Pour une affiliation, date d'effet, garantie choisie, option facultative, date d'entrée dans lentreprise / collectivité",
  },
  removal: {
    slug: 'removal',
    label: 'Radiation',
    description: "Pour une radiation, date d'effet, motif, portabilité",
  },
  accept_data_use: {
    slug: 'accept_data_use',
    label:
      "Acceptation d'utilisation des données pour utilisation par la mutuelle",
    description: null,
  },
  accept_prospection: {
    slug: 'accept_prospection',
    label: "Acceptation d'utilisation des données pour prospection",
    description: null,
  },
};

export const FILE_TYPES = {
  pdf: {
    type: 'application/pdf',
    label: 'PDF',
  },
  jpg: {
    type: 'image/jpeg',
    label: 'JPG',
  },
  png: {
    type: 'image/png',
    label: 'PNG',
  },
  doc: {
    type: 'application/msword',
    label: 'DOC',
  },
  docx: {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    label: 'DOCX',
  },
  xls: {
    type: 'application/vnd.ms-excel',
    label: 'XLS',
  },
  xlsx: {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    label: 'XLSX',
  },
  csv: {
    type: 'text/csv',
    label: 'CSV',
  },
  zip: {
    type: 'application/zip',
    label: 'ZIP',
  },
};

export const MEMBER_STATUSES = {
  0: {
    slug: 'active',
    label: 'Actif',
    icon: 'check2',
    value: 0,
  },
  1: {
    slug: 'nd',
    label: 'ND',
    icon: 'x',
    value: 1,
  },
  2: {
    slug: 'future',
    label: 'Futur',
    icon: 'clock',
    value: 2,
  },
  3: {
    slug: 'removed',
    label: 'Radié',
    icon: 'x',
    value: 3,
  },
  4: {
    slug: 'no-effect',
    label: 'Sans effet',
    icon: 'x',
    value: 4,
  },
  5: {
    slug: 'future-active-removed',
    label: 'Actif futur radié',
    icon: 'clock-history',
    value: 5,
  },
  11: {
    slug: 'portability',
    label: 'Portabilité',
    icon: 'person-walking',
    value: 11,
  },
};

export const USER_ACCESS_TYPES = {
  mutuelle: {
    label: 'Mutuelle',
    value: 'mutuelle',
  },
  enterprise: {
    label: 'Entreprise',
    value: 'enterprise',
  },
};

export const USER_ROLES = {
  admin: {
    label: 'Admin',
    value: 'admin',
  },
  manager: {
    label: 'Gestionnaire',
    value: 'manager',
  },
  readonly: {
    label: 'Consultant',
    value: 'readonly',
  },
};

export class AuthStatus {
  constructor(
    public readonly authDone: boolean,
    public readonly logged: boolean,
    public readonly userId: number = null,
    public readonly token: string = null,
    public readonly enterprise: Enterprise = null,
    public readonly user: User = null,
  ) {}
}

export class Enterprise {
  id: string;
  name: string;
  code: string;
  siret: string;
  siren: string;
  ape: string;
  vatNumber: string;
  creationDate: string;
  state: string;
  administrator: Contact;

  constructor(obj: any = null) {
    Object.assign(this, obj);
    this.id = String(obj.id);
    this.vatNumber = obj.tva;
    this.administrator = obj.administrator
      ? new Contact(obj.administrator)
      : null;
  }
}

export class Contact {
  firstname: string;
  lastname: string;
  phone: string;
  mobile: string;
  mail: string;
  job: string;
  address: Address;

  constructor(obj: any = null) {
    Object.assign(this, obj);
    this.address = obj.address ? new Address(obj.address) : null;
  }
}

export class EnterpriseStatistics {
  insureds: {
    opened: number;
    future: number;
    closed: number;
    noEffect: number;
    futureClosed: number;
  };
  beneficiaires: {
    opened: number;
    future: number;
    closed: number;
  };
  portabilite: number;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class User {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  mobile: string;
  job: string;
  enterpriseId: string;
  enterpriseName: string;
  passwordMustBeChanged: boolean;
  role: 'admin' | 'manager' | 'readonly';
  accessType: 'mutuelle' | 'enterprise';

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }

  initials(): string {
    return this.firstname?.charAt(0) + this.lastname?.charAt(0);
  }

  roleFormatted() {
    return (
      USER_ROLES[this.role].label +
      ' ' +
      USER_ACCESS_TYPES[this.accessType].label.toLowerCase()
    );
  }

  get fullName(): string {
    return this.firstname + ' ' + this.lastname;
  }
}

export class Population {
  id: string;
  name: string;
  code: string;
  typeName: string;
  typeCode: string;
  members: Insured[];

  constructor(obj: any = null) {
    Object.assign(this, obj);
    this.members = obj?.members?.map((m) => new Insured(m)) || [];
  }
}

export class Insured {
  id: string;
  civility: string;
  birthname: string;
  firstname: string;
  lastname: string;
  birthdate: string;
  birthPlace: string;
  insee: string;
  inseeKey: string;
  inseeType: 'conjoint' | 'assure' | 'autre';
  sex: string;
  lunarBirthdate: string;
  regime: string;
  beneficiariesCount: string;
  socialSecurityNumber: string;
  startDate: string;
  endDate: string;
  valid: boolean;
  attachment: InsuredAttachment;
  status: number;
  contract: Contract;
  beneficiaries: Insured[];
  teletransmission: {
    code: string;
    label: string;
  };
  bank: BankDetails;
  phone: string;
  mobile: string;
  mail: string;
  address: Address;
  fullname: string;
  cotisations: Cotisation[];
  type: string;
  codeAyantDroit: string;
  lastAccess: string;
  beneficiariesCountDetails: {
    opened: number;
    closed: number;
    future: number;
  };
  population: Population;
  canShowBeneficiaires: boolean;
  contact: Contact;
  rank: number;

  constructor(obj: any = null) {
    Object.assign(this, obj);
    if (obj?.id != null) {
      this.id = String(obj.id);
    }
    this.attachment = obj?.attachment
      ? new InsuredAttachment(obj.attachment)
      : null;
    this.contract = obj?.contract ? new Contract(obj.contract) : null;
    this.beneficiaries = obj?.beneficiaries?.map((b) => new Insured(b)) || [];
    this.bank = obj?.bank ? new BankDetails(obj.bank) : null;
    this.address = obj?.address ? new Address(obj.address) : null;
    this.cotisations = obj?.cotisations?.map((c) => new Cotisation(c)) || [];
    this.startDate = new EeDatePipe().transform(this.startDate);
    this.endDate = new EeDatePipe().transform(this.endDate);
    // Utils.fillObject(this, 'Non disponible');
  }

  initials(): string {
    return this.lastname?.charAt(0) + this.firstname?.charAt(0);
  }

  statusAsObject() {
    return MEMBER_STATUSES[this.status];
  }

  fullnameFormatted(): string {
    return this.fullname || this.firstname + ' ' + this.lastname;
  }

  typeFormatted(): string {
    switch (this.type) {
      case 'CCC':
      case 'CC':
      case 'C':
      case 'CJ':
        return 'Conjoint•e';
      case 'E':
        return 'Enfant';
      case 'A':
        return 'Assuré•e';
      default:
        return 'Inconnu';
    }
  }

  lastAccessFormatted(): string {
    return this.lastAccess
      ? moment(this.lastAccess).format('DD/MM/YYYY HH:mm')
      : 'Jamais';
  }
}

export class InsuredAttachment {
  caisse: string;
  regime: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class Address {
  type: string;
  street: string;
  zipCode: string;
  city: string;
  building: string;
  floor: string;
  locality: string;
  country: string;
  recipient: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }

  formatted() {
    return this.street + ' ' + this.zipCode + ' ' + this.city;
  }
}

export class Country {
  id: number;
  code2Char: string;
  code3Char: string;
  name: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class Department {
  id: number;
  code: string;
  codeInsee: string;
  name: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class City {
  inseeCode: string;
  cityCode: string;
  zipCode: string;
  label: string;
  latitude: string;
  longitude: string;
  departmentName: string;
  departmentNumber: string;
  regionName: string;
  regionGeojsonName: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class Caisse {
  label: string;
  labelDetail: string;
  zipCode: string;
  city: string;
  codeCaisse: string;
  codeRegime: string;
  code: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class Contract {
  id: string;
  name: string;
  code: string;
  startDate: string;
  endDate: string;
  paymentModeCode: string;
  paymentMode: string;
  paymentPeriodicityCode: string;
  paymentPeriodicity: string;
  callTypCode: string;
  callType: string;
  calculationMethodCode: string;
  calculationMethod: string;
  state: string;

  constructor(obj: any = null) {
    Object.assign(this, obj);
    this.startDate = new EeDatePipe().transform(this.startDate);
    this.endDate = new EeDatePipe().transform(this.endDate);
  }
}

export class BankDetails {
  name: string;
  holder: string;
  iban: string;
  bic: string;
  ribAccountNumber: string;
  ribKey: string;
  ribBankCode: string;
  ribBranchCode: string;
  ribCountryCode: string;
  domiciliation: string;
  valid: boolean;

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }
}

export class Share {
  uuid: string;
  insuredId: string;
  insuredName: string;
  enterpriseId: string;
  enterpriseName: string;
  target: string;
  source: string;
  expiredAt: string;
  createdAt: string;
  lastAccess: string;
  documents: Document[];
  message: string;
  type: string;
  sourceArchived: boolean;
  targetArchived: boolean;

  constructor(obj: any = null) {
    Object.assign(this, obj);
    this.documents = obj?.documents?.map((e) => new Document(e)) || [];
    this.message = this.message && this.message !== '' ? this.message : 'Aucun';
  }

  createdAtFormatted() {
    return moment(this.createdAt).format('DD/MM/YYYY HH:mm');
  }

  isSentByMe(user: User) {
    return user?.accessType === this.source;
  }

  targetFormatted(): string {
    switch (this.source) {
      case 'mutuelle':
        return this.enterpriseName || 'Inconnu';
      case 'enterprise':
        return this.insuredName || environment.mutuelleCode;
    }
    return 'Inconnu';
  }

  sourceFormatted(user: User): string {
    switch (user?.accessType) {
      case 'mutuelle':
        return this.enterpriseName || 'Inconnu';
      case 'enterprise':
        return this.insuredName || environment.mutuelleCode;
    }
    return 'Inconnu';
  }

  isSentOrReceivedByInsured() {
    return this.source === 'insured' || this.target === 'insured';
  }

  isSentOrReceivedByMutuelle() {
    return this.source === 'mutuelle' || this.target === 'mutuelle';
  }

  isSentOrReceivedByEnterprise() {
    return this.source === 'enterprise' || this.target === 'enterprise';
  }

  canArchive(user: User) {
    return (
      user?.role !== 'readonly' &&
      ((this.source === user.accessType && !this.sourceArchived) ||
        (user.accessType === this.target && !this.targetArchived))
    );
  }
}

export class Document {
  uuid: string;
  title: string;
  createdAt: string;
  createdBy: string;
  deleted: boolean;
  enterpriseId: string;
  insuredId: string;
  description: string;
  /** @deprecated for shares listing; not set on library API rows */
  source: 'mutuelle' | 'enterprise' | 'insured';
  /** Library row provenance from API (`library` = DB upload, `warranty` = Synergie RDG). */
  origin?: 'library' | 'warranty';
  filesCount: number;
  fileName: string;
  size: number;
  extension: string;
  toBeCompleted: boolean;

  get icon(): string {
    if (this.origin === 'warranty') {
      return 'ti ti-shield-check';
    }
    switch (this.extension) {
      case 'pdf':
        return 'ti ti-file-type-pdf';
      case 'doc':
      case 'docx':
        return 'ti ti-file-type-docx';
      case 'xls':
      case 'xlsx':
        return 'ti ti-file-type-xls';
      default:
        return 'ti ti-photo';
    }
  }

  constructor(obj: any = null) {
    Object.assign(this, obj);
  }

  private isWarrantyRow(): boolean {
    return (
      this.origin === 'warranty' ||
      (typeof this.uuid === 'string' && this.uuid.startsWith('warranty:'))
    );
  }

  canDelete(user: User) {
    if (this.isWarrantyRow()) {
      return false;
    }
    return user.role !== 'readonly';
  }

  canUpdate(user: User) {
    if (this.isWarrantyRow()) {
      return false;
    }
    return user.role !== 'readonly';
  }

  canDownload(user: User) {
    return !!user;
  }

  sizeFormatted() {
    if (this.origin === 'warranty' && (this.size == null || this.size === 0)) {
      return '—';
    }
    if (!this.size) {
      return '';
    }
    if (this.size > 1024 * 1024) {
      return (this.size / 1024 / 1024).toFixed(2) + ' MB';
    } else if (this.size > 1024) {
      return (this.size / 1024).toFixed(2) + ' KB';
    } else {
      return this.size + ' B';
    }
  }
}

/**
 * @deprecated Use document directly instead
 */
export class DocumentFile {}

export class PaginatedList<T> {
  items: T[];
  page: number;
  perPage: number;
  total: number;

  constructor(obj: any, type: new (obj: any) => T) {
    Object.assign(this, obj);
    this.items = obj.items?.map((i) => new type(i));
  }
}

export class Cotisation {
  label: string;
  amount: number;
  createdAt: string;
  dueAt: string;
  type: number; // 0: cotisation, 1: remboursement
  frequency: string;
  status: 'paid' | 'unpaid';
  amountDue: number;

  constructor(obj: any = null) {
    if (obj) {
      Object.assign(this, obj);
    }
  }

  get statusFormatted(): string {
    return this.status === 'paid'
      ? 'Payée'
      : 'Reste : ' + this.amountDue + ' €';
  }
}

export class EnterpriseCotisation {
  identifier: number;
  label: string;
  type: number;
  creationDate: Date | null;
  callDate: Date | null;
  paymentMode: {
    code: string;
    label: string;
  } | null;
  frequency: {
    code: string;
    label: string;
  } | null;
  debit: number | null;
  credit: number | null;
  amount: number | null;
  balance: number | null;
  contributorAccount: number | null;
  contract: {
    code: string;
    label: string;
  } | null;
  domain: number | null;
  details: Array<{
    option: {
      code: string;
      label: string;
    } | null;
    debit: number | null;
    credit: number | null;
    amount: number | null;
    balance: number | null;
    startDate: Date | null;
    endDate: Date | null;
    contributorClass: {
      code: string;
      label: string;
    } | null;
    salaryRange: {
      code: string;
      label: string;
    } | null;
  }> | null;
  lettering: string | null;
  status: 'paid' | 'unpaid';

  constructor(obj: Partial<EnterpriseCotisation> = null) {
    if (obj) {
      Object.assign(this, obj);
      this.status = this.balance > 0 ? 'unpaid' : 'paid';
    }
  }

  get statusFormatted(): string {
    return this.status === 'paid'
      ? 'Payée'
      : 'Reste : ' + this.balance.toFixed(2) + ' €';
  }
}

// Defined document from various GED
export class DocumentGED {
  identifier: number | null;
  label: string | null;
  type: string | null;
  fileName: string | null;
  extension: string | null;
  date: string | null;
  creationDate: string | null;
  modificationDate: string | null;

  constructor(obj: Partial<DocumentGED> = null) {
    Object.assign(this, obj);
  }
}

export class PersonnelMovement {
  id: number;
  uuid: string;
  createdAt: string;
  exitDate: string;
  entryDate: string;
  insuredId: string;
  insuredName: string;
  enterpriseId: string;
  enterpriseName: string;
  portabilite: boolean;

  constructor(obj: Partial<PersonnelMovement>) {
    Object.assign(this, obj);
  }
}

export class MemberFilters {
  status?: number[];
  caisse?: string[];
  // contract: string[];
  startDate?: string;
  endDate?: string;
  population?: string[];
}

export class Rdg {
  options: SelectItemInterface[];

  constructor(obj: Partial<Rdg>) {
    Object.assign(this, obj);
  }
}

export class Term {
  title: string;
  content: string;
  position: number;

  constructor(obj: Partial<Term>) {
    Object.assign(this, obj);
  }
}
